/**
 * PronunciationPrompt — shows a reference text card above the input area
 * when the AI agent triggers the `pronunciation_assessment` directive.
 *
 * The user can speak (mic button) or type — both dismiss the prompt.
 * If `reference_text` is present, the mic uses scripted assessment mode.
 */

import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text, HStack } from '@gluestack-ui/themed';
import Ionicons from '../ui/Ionicons';
import { useTranslation } from 'react-i18next';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import type { ChatDirectives } from '../../types/conversation';

interface PronunciationPromptProps {
  directive: NonNullable<ChatDirectives['pronunciation_assessment']>;
  onDismiss: () => void;
}

const PronunciationPrompt = React.memo(function PronunciationPrompt({
  directive,
  onDismiss,
}: PronunciationPromptProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();

  return (
    <View
      testID="chat-pronunciation-prompt-container"
      accessibilityRole="text"
      accessibilityLabel={
        directive.reference_text
          ? t('chat.pronunciation.referenceLabel', { text: directive.reference_text })
          : t('chat.pronunciation.freeLabel')
      }
      style={[styles.container, { backgroundColor: theme.card, borderColor: theme.primary }]}
    >
      <HStack alignItems="center" justifyContent="space-between">
        <HStack space="sm" alignItems="center" style={styles.content}>
          <Ionicons name="mic" size={18 * fontScale} color={theme.primary} />
          <View style={styles.textContainer}>
            <Text
              allowFontScaling={false}
              style={[
                styles.hint,
                { color: theme.textSecondary, fontSize: 12 * fontScale, ...ffs },
              ]}
            >
              {t('chat.pronunciation.hint')}
            </Text>
            {directive.reference_text ? (
              <Text
                testID="chat-pronunciation-prompt-reference-text"
                allowFontScaling={false}
                style={[
                  styles.referenceText,
                  { color: theme.text, fontSize: 16 * fontScale, ...ffs },
                ]}
              >
                {directive.reference_text}
              </Text>
            ) : null}
          </View>
        </HStack>
        <TouchableOpacity
          testID="chat-pronunciation-prompt-dismiss"
          accessibilityLabel={t('chat.pronunciation.dismiss')}
          accessibilityRole="button"
          onPress={onDismiss}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Ionicons name="close" size={18 * fontScale} color={theme.textSecondary} />
        </TouchableOpacity>
      </HStack>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 10,
    marginBottom: 4,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 16,
    borderWidth: 1.5,
  },
  content: {
    flex: 1,
    marginRight: 8,
  },
  textContainer: {
    flex: 1,
  },
  hint: {
    lineHeight: 16,
  },
  referenceText: {
    fontWeight: '600',
    lineHeight: 22,
    marginTop: 2,
  },
});

export default PronunciationPrompt;
